import {
  ComponentTemplateDto,
  ComponentTemplatePrivacyType,
} from "@growth-machine-llc/stridist-api-client";
import { MoreHoriz } from "@mui/icons-material";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuProps,
} from "@mui/material";
import React from "react";
import { clsx } from "clsx";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useDeleteComponentTemplateLibraryMutation } from "./hooks/useDeleteLibraryComponentTempleteLibrary";
import { Pencil, Trash2 } from "lucide-react";
import { Menu } from "../menu/Menu";

export interface ComponentTemplateMenuProps
  extends Omit<MenuProps, "open" | "onClose" | "component"> {
  component: ComponentTemplateDto;
  className?: string;
  onEdit?: () => void;
}

export const ComponentTemplateMenu = (props: ComponentTemplateMenuProps) => {
  const { component, className, onEdit, ...other } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const { mutate: removeTemplate } =
    useDeleteComponentTemplateLibraryMutation();

  const [
    confirmComponentTemplateDeleteDialogOpen,
    setConfirmComponentTemplateDeleteDialogOpen,
  ] = React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleEdit = React.useCallback(() => {
    if (onEdit) {
      onEdit();
    }
    setAnchorEl(null);
  }, [onEdit]);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleComponentDelete = React.useCallback(() => {
    setAnchorEl(null);

    // TODO temporary disable confirmation to speed up delete actions
    setConfirmComponentTemplateDeleteDialogOpen(true);
  }, [component, setConfirmComponentTemplateDeleteDialogOpen]);

  const handleConfirmExerciseDelete = React.useCallback(() => {
    removeTemplate(component.id);
    setConfirmComponentTemplateDeleteDialogOpen(false);
  }, [component]);

  const handleCancelExerciseDelete = React.useCallback(() => {
    setConfirmComponentTemplateDeleteDialogOpen(false);
  }, []);
  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <Menu
        id={component.id.toString()}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        className={clsx(className)}
        {...other}
      >
        <MenuItem onClick={handleEdit} dense>
          <ListItemIcon children={<Pencil />} />
          <ListItemText>Edit template</ListItemText>
        </MenuItem>
        {component.privacy === ComponentTemplatePrivacyType.PRIVATE && (
          <MenuItem onClick={handleComponentDelete} dense>
            <ListItemIcon children={<Trash2 />} />
            <ListItemText>Delete template</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <ConfirmActionDialog
        title={"Are you sure you want to delete component template?"}
        open={confirmComponentTemplateDeleteDialogOpen}
        onClose={handleCancelExerciseDelete}
        onCancel={handleCancelExerciseDelete}
        onConfirm={handleConfirmExerciseDelete}
      />
    </>
  );
};
